import { useFieldApi } from 'informed';
import type { Form } from 'payments-lib-types';
import { type FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { FormGroup } from '@/components/form/groups/FormGroup';
import { Input } from '@/components/form/Input';
import { DEFAULT_CURRENCIES } from '@/helpers/currency';
import {
  amountLimit,
  composeValidators,
  maskAmount,
  required,
  unmaskAmount,
  validateMaxAmount,
} from '@/helpers/validators';
import { useMaxAmountValue } from '@/hooks/wallet';
import { useGetCommonStore, useWithdrawStore } from '@/store';
import type { OfUnion } from '@/types/common';

export const FieldAmount: FC<
  OfUnion<Form.MethodField>[Form.MethodFieldType.Amount]
> = ({ fieldName }) => {
  const { t } = useTranslation(['deposits', 'validation']);
  const useCommonStore = useGetCommonStore();

  const user = useCommonStore.use.user();
  const {
    activeThemeMode,
    currentPaymentMethod,
    currentAmount,
    defaultAmountRestrictions,
    setCurrentAmount,
  } = useWithdrawStore(
    useShallow((state) => ({
      activeThemeMode: state.activeThemeMode,
      currentPaymentMethod: state.currentPaymentMethod,
      currentAmount: state.currentAmount,
      defaultAmountRestrictions: state.defaultAmountRestrictions,
      setCurrentAmount: state.setCurrentAmount,
    }))
  );

  const fieldApi = useFieldApi(fieldName);

  useEffect(() => {
    if (!(fieldApi && fieldApi.setValue) || !!fieldApi.getValue()) return;
    fieldApi.setValue(currentAmount);
  }, [fieldApi, currentAmount]);

  const maxAmountValue = useMaxAmountValue();

  const currentAmountValidation = useMemo(() => {
    const limits =
      currentPaymentMethod?.termsOfService.restrictions ||
      defaultAmountRestrictions;

    if (!limits) return required;

    const unmaskAmountWithCurrency = (valueWithMask: string) =>
      unmaskAmount(valueWithMask).toString();

    return composeValidators(
      required,
      amountLimit(limits, unmaskAmountWithCurrency),
      maxAmountValue
        ? validateMaxAmount(maxAmountValue, unmaskAmountWithCurrency)
        : () => {}
    );
  }, [
    defaultAmountRestrictions,
    maxAmountValue,
    currentPaymentMethod?.termsOfService.restrictions,
  ]);

  const maskAmountWithCurrency = useCallback(
    (value: string) => {
      const valueWithMask = maskAmount(value);

      if (!user?.currency) return valueWithMask;
      return valueWithMask;
    },
    [user?.currency]
  );

  return (
    <FormGroup mode={activeThemeMode} title={t('deposits:fields.amount')}>
      <Input
        initialValue={currentAmount.toString()}
        internalLabel={user?.currency}
        mode={activeThemeMode}
        name={fieldName}
        onChange={({ target }) =>
          setCurrentAmount(user, DEFAULT_CURRENCIES, maskAmount(target.value))
        }
        parser={maskAmountWithCurrency}
        placeholder={t('deposits:fields.amount')}
        validate={currentAmountValidation}
        validateOn="change"
      />
    </FormGroup>
  );
};
